import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from './Button'
import theme from '../styles/theme'
import {
  classifyMergeBackupClaims,
  MT_MERGE_OPERATION_STORAGE_KEY,
  parseMtMergeOperationStorage,
  StoredMergeOperation,
} from './mtMergeState'

const MergeContainer = styled.div`
  background: ${theme.color.background.frame};
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 16px ${theme.color.shadow.medium},
              0 4px 8px ${theme.color.shadow.light};
  margin-bottom: 2rem;
`

const MergeTitle = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`

const InputSection = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  align-items: end;
  flex-wrap: wrap;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 250px;
`

const Label = styled.label`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const Input = styled.input`
  padding: 1rem;
  border: 2px solid ${theme.color.border.light};
  border-radius: 8px;
  font-size: 1.4rem;
  background: ${theme.color.background.light};
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
    box-shadow: inset 0 2px 4px ${theme.color.shadow.light}, 0 0 0 3px rgba(215, 0, 54, 0.1);
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`

const ProfilesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 1400px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const ProfileCard = styled.div<{ $type: 'current' | 'merge' | 'result'; $selected?: boolean }>`
  border: 3px solid ${props =>
    props.$type === 'current' ? '#4CAF50' :
    props.$type === 'merge' ? '#FF9800' :
    '#2196F3'};
  border-radius: 12px;
  padding: 1.5rem;
  background: ${props => props.$selected ? 'rgba(76, 175, 80, 0.05)' : theme.color.background.light};
  box-shadow: ${props => props.$selected
    ? '0 4px 12px rgba(76, 175, 80, 0.3)'
    : `0 2px 6px ${theme.color.shadow.light}`};
  transition: all 0.2s ease;
`

const CardHeader = styled.div<{ $type: 'current' | 'merge' | 'result' }>`
  background: ${props =>
    props.$type === 'current' ? '#4CAF50' :
    props.$type === 'merge' ? '#FF9800' :
    '#2196F3'};
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-weight: 600;
  font-size: 1.4rem;
`

const RadioSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${theme.color.background.frame};
  border-radius: 8px;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.color.background.lightGrey};
  }
`

const RadioButton = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`

const RadioLabel = styled.label`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.accent.primary};
  cursor: pointer;
`

const RadioSpacer = styled.div`
  height: calc(20px + 2rem);
  margin-bottom: 1.5rem;
`

const Section = styled.div`
  margin-bottom: 1.5rem;
`

const SectionTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.color.accent.primary};
  margin-bottom: 0.75rem;
  border-bottom: 1px solid ${theme.color.border.light};
  padding-bottom: 0.5rem;
`

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid ${theme.color.border.light};
  align-items: start;

  &:last-child {
    border-bottom: none;
  }
`

const FieldLabel = styled.div`
  font-weight: 500;
  color: ${theme.color.text.base};
  font-size: 1.2rem;
`

const FieldValue = styled.div`
  color: ${theme.color.text.subtext};
  font-size: 1.2rem;
  word-break: break-word;
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`

const SuccessMessage = styled.div`
  background: #e6ffe6;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
`

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: ${theme.color.background.light};
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.color.border.light};
  border-top: 4px solid ${theme.color.accent.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const LoadingText = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  text-align: center;
`

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const Tag = styled.span`
  background: ${theme.color.background.lightGrey};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 1.1rem;
  color: ${theme.color.text.base};
`

const CreditCardItem = styled.div`
  background: ${theme.color.background.frame};
  padding: 0.5rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`

const TotalRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid ${theme.color.border.light};
  align-items: center;
  background: ${theme.color.background.frame};
  margin: 0 -0.5rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;

  &:last-child {
    border-bottom: none;
  }
`

const TotalValue = styled.div`
  color: ${theme.color.accent.primary};
  font-size: 1.3rem;
  font-weight: 600;
`

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`

const ModalTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.accent.primary};
  margin-bottom: 1.5rem;
  text-align: center;
`

const ModalText = styled.p`
  font-size: 1.4rem;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
  text-align: center;
  line-height: 1.6;
`

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`

const MergeButton = styled(Button)`
  background: #4CAF50;
  border-color: #4CAF50;

  &:hover:not(:disabled) {
    background: #43A047;
    border-color: #43A047;
  }
`

interface MTMergeProps {
  userData?: any
}

const WarningBanner = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 1.5rem;
  border-radius: 8px;
  border: 2px solid #ffc107;
  margin-bottom: 1.5rem;
  font-size: 1.4rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 1rem;
`

const BlockBanner = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1.5rem;
  border-radius: 8px;
  border: 2px solid #dc3545;
  margin-bottom: 1.5rem;
  font-size: 1.4rem;
  font-weight: 600;
`

const ValidationSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: ${theme.color.background.light};
  border-radius: 8px;
  border: 1px solid ${theme.color.border.light};
`

const ValidationRow = styled.div<{ $status: 'pass' | 'fail' | 'loading' }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${theme.color.border.light};
  font-size: 1.3rem;

  &:last-child {
    border-bottom: none;
  }
`

const ValidationIcon = styled.span<{ $status: 'pass' | 'fail' | 'loading' }>`
  font-size: 1.6rem;
`

const ValidationLabel = styled.span`
  font-weight: 500;
  color: ${theme.color.text.base};
`

const ValidationDetail = styled.span`
  color: ${theme.color.text.grey};
  font-size: 1.2rem;
`

interface ProfileData {
  marianaTekId: string
  name: string
  email: string
  accountType: string
  phone: string
  address: string
  birthday: string
  creditCards: any[]
  lastClassTaken: string
  accountCreated: string
  topStudio: string
  homeLocation: string
  mergedDetails: string
  tags: string[]
  accountBalances: number
  purchasesCount: number
  upcomingReservationsCount: number
  classesTakenCount: number
  creditsCount: number
  activeMembershipsCount: number
  notesCount: number
  rawData?: any
  isEmployee?: boolean
  employeeStatus?: string
  isMerged?: boolean
}

interface MergeValidation {
  currentAccountActive: boolean | null
  mergeAccountActive: boolean | null
  currentNotEmployee: boolean | null
  mergeNotEmployee: boolean | null
  currentEmployeeDetail: string
  mergeEmployeeDetail: string
  canMerge: boolean
}

type ProfileSelection = 'current' | 'merge'

type MergeOperationStatus =
  | 'backing_up'
  | 'backup_failed'
  | 'backup_in_doubt'
  | 'submitting'
  | 'acceptance_unknown'
  | 'pending'
  | 'applying'
  | 'succeeded'
  | 'rejected'
  | 'in_doubt'

interface MergeBackupProof {
  bucket: string
  key: string
  version_id: string
  etag: string
  sha256: string
  size: number
  operation_id: string
  keep_user_id: string
  merge_user_id: string
  role: 'keep' | 'merge'
  subject_user_id: string
}

type PersistedMergeOperation = StoredMergeOperation

const MERGE_OPERATION_STORAGE_KEY = MT_MERGE_OPERATION_STORAGE_KEY
const GATEWAY_MERGE_OPERATION_STATUSES = new Set<MergeOperationStatus>([
  'pending',
  'applying',
  'succeeded',
  'rejected',
  'in_doubt',
])

// Walk an MT JSON:API endpoint across pages. Bounded by maxPages to keep latency sane.
const fetchAllPagesMT = async (endpoint: string, maxPages = 20): Promise<any[]> => {
  const all: any[] = []
  let page = 1
  let totalPages = 1
  while (page <= totalPages && page <= maxPages) {
    const sep = endpoint.includes('?') ? '&' : '?'
    const url = `${endpoint}${sep}page=${page}&page_size=100`
    const res = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
    if (!res.ok) break
    const data = await res.json()
    if (Array.isArray(data?.data)) all.push(...data.data)
    totalPages = data?.meta?.pagination?.pages || 1
    page++
  }
  return all
}

const MTMerge: React.FC<MTMergeProps> = ({ userData }) => {
  const [mergeEmail, setMergeEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isMerging, setIsMerging] = useState(false)
  const [mergeStatus, setMergeStatus] = useState('')
  const [activeMergeOperation, setActiveMergeOperation] = useState<PersistedMergeOperation | null>(null)
  const mergeSubmissionInFlight = React.useRef(false)
  const operationLabel = (operation: PersistedMergeOperation) => (
    `MT ${operation.mergeUserId} into MT ${operation.keepUserId}`
  )
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [currentProfile, setCurrentProfile] = useState<ProfileData | null>(null)
  const [mergeProfile, setMergeProfile] = useState<ProfileData | null>(null)
  const [selectedProfile, setSelectedProfile] = useState<ProfileSelection>('current')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [validation, setValidation] = useState<MergeValidation>({
    currentAccountActive: null,
    mergeAccountActive: null,
    currentNotEmployee: null,
    mergeNotEmployee: null,
    currentEmployeeDetail: '',
    mergeEmployeeDetail: '',
    canMerge: false
  })

  // Get the MarianaTek ID from the current user
  const currentMarianaTekId = userData?.user?.external_id || userData?.external_ids?.mariana_tek

  // Check if a user is an employee via MT API
  const checkEmployeeStatus = async (mtId: string): Promise<{ isEmployee: boolean | null; detail: string }> => {
    try {
      const response = await fetch(`/api/marianatek/api/employees/?users=${mtId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      if (!response.ok) {
        return { isEmployee: null, detail: 'Unable to verify employee status' }
      }
      const data = await response.json()
      const employees = data.data || []
      if (employees.length === 0) {
        return { isEmployee: false, detail: 'Not an employee' }
      }
      const emp = employees[0]
      const isActive = emp.attributes?.is_active
      return {
        isEmployee: true,
        detail: `Employee ID: ${emp.id} (${isActive ? 'Active' : 'Inactive'})`
      }
    } catch {
      return { isEmployee: null, detail: 'Unable to verify employee status' }
    }
  }

  // Check if an account has been merged
  const isAccountMerged = (profile: ProfileData): boolean => {
    return !!profile.rawData?.attributes?.merged_into_id
  }

  // Load current profile when component mounts or userData changes
  useEffect(() => {
    if (currentMarianaTekId) {
      loadProfile(currentMarianaTekId, 'current')
    }
  }, [currentMarianaTekId])

  const loadProfile = async (marianaTekId: string, profileType: 'current' | 'merge') => {
    try {
      // Fetch main user data
      const userResponse = await fetch(`/api/marianatek/api/users/${marianaTekId}/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!userResponse.ok) {
        throw new Error(`Failed to fetch user: ${userResponse.status}`)
      }

      const userData = await userResponse.json()
      const user = userData.data
      const included = userData.included || []

      // Fetch credit cards (bank cards)
      let creditCards: any[] = []
      try {
        const cardsResponse = await fetch(`/api/marianatek/api/bank_cards/?user=${marianaTekId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
        if (cardsResponse.ok) {
          const cardsData = await cardsResponse.json()
          creditCards = cardsData.data || []
        }
      } catch (e) {
        console.error('Failed to fetch credit cards:', e)
      }

      // Reservations: walk all pages so counts and last-class are correct for heavy users.
      let upcomingReservationsCount = 0
      let lastClassTaken = 'N/A'
      try {
        const reservations = await fetchAllPagesMT(`/api/marianatek/api/reservations/?user=${marianaTekId}`)

        upcomingReservationsCount = reservations.filter((r: any) => {
          const status = r.attributes?.status?.toLowerCase()
          return status === 'reserved' || status === 'waitlisted'
        }).length

        let latest = 0
        for (const r of reservations) {
          if (r.attributes?.status?.toLowerCase() !== 'check in') continue
          const t = new Date(r.attributes?.check_in_date || 0).getTime()
          if (t > latest) latest = t
        }
        if (latest > 0) lastClassTaken = new Date(latest).toLocaleDateString()
      } catch (e) {
        console.error('Failed to fetch reservations:', e)
      }

      // Lifetime classes-taken: trust MT's authoritative counter (avoid recomputing from paginated reservations).
      const completedClassesCount = user.attributes?.completed_class_count ?? 0

      // Credits: paginate so heavy users aren't capped at the first page.
      let creditsCount = 0
      try {
        const credits = await fetchAllPagesMT(`/api/marianatek/api/credit_transactions/?user=${marianaTekId}`)
        creditsCount = credits.filter((c: any) => c.attributes?.remaining_credit_count > 0).length
      } catch (e) {
        console.error('Failed to fetch credits:', e)
      }

      // Active memberships: paginate.
      let activeMembershipsCount = 0
      try {
        const memberships = await fetchAllPagesMT(`/api/marianatek/api/membership_instances/?user=${marianaTekId}`)
        activeMembershipsCount = memberships.filter((m: any) => m.attributes?.state === 'active').length
      } catch (e) {
        console.error('Failed to fetch memberships:', e)
      }

      // Orders (purchases): paginate.
      let purchasesCount = 0
      try {
        const orders = await fetchAllPagesMT(`/api/marianatek/api/orders/?user=${marianaTekId}`)
        purchasesCount = orders.length
      } catch (e) {
        console.error('Failed to fetch orders:', e)
      }

      // Notes: paginate.
      let notesCount = 0
      try {
        const notes = await fetchAllPagesMT(`/api/marianatek/api/notes/?user=${marianaTekId}`)
        notesCount = notes.length
      } catch (e) {
        console.error('Failed to fetch notes:', e)
      }

      // Extract tags from included data or relationships
      const tagIds = user.relationships?.tags?.data?.map((t: any) => t.id) || []
      const tagNames = included
        .filter((inc: any) => inc.type === 'user_tags' && tagIds.includes(inc.id))
        .map((t: any) => t.attributes?.name || `Tag ${t.id}`)

      // Get home location
      const homeLocationId = user.relationships?.home_location?.data?.id
      const homeLocation = included.find((inc: any) =>
        inc.type === 'locations' && inc.id === homeLocationId
      )?.attributes?.name || 'N/A'

      // Build profile data
      const profile: ProfileData = {
        marianaTekId: marianaTekId,
        name: user.attributes?.full_name || `${user.attributes?.first_name || ''} ${user.attributes?.last_name || ''}`.trim(),
        email: user.attributes?.email || 'N/A',
        accountType: user.attributes?.is_external_user ? 'External' : 'Standard',
        phone: user.attributes?.phone_number || 'N/A',
        address: formatAddress(user.attributes),
        birthday: user.attributes?.birth_date ? new Date(user.attributes.birth_date).toLocaleDateString() : 'N/A',
        creditCards: creditCards,
        lastClassTaken: lastClassTaken,
        accountCreated: user.attributes?.date_joined ? new Date(user.attributes.date_joined).toLocaleDateString() : 'N/A',
        topStudio: homeLocation,
        homeLocation: homeLocation,
        mergedDetails: user.attributes?.merged_into_id ? `Merged into: ${user.attributes.merged_into_id}` : 'Not merged',
        tags: tagNames,
        accountBalances: user.attributes?.account_balance || 0,
        purchasesCount: purchasesCount,
        upcomingReservationsCount: upcomingReservationsCount,
        classesTakenCount: completedClassesCount,
        creditsCount: creditsCount,
        activeMembershipsCount: activeMembershipsCount,
        notesCount: notesCount,
        rawData: user
      }

      if (profileType === 'current') {
        setCurrentProfile(profile)
      } else {
        setMergeProfile(profile)
      }
    } catch (err) {
      console.error(`Failed to load ${profileType} profile:`, err)
      throw err
    }
  }

  const formatAddress = (attrs: any): string => {
    if (!attrs) return 'N/A'
    const parts = [
      attrs.address_line1,
      attrs.address_line2,
      attrs.city,
      attrs.state_province,
      attrs.postal_code,
      attrs.country
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : 'N/A'
  }

  const runValidation = async (currentMtId: string, mergeMtId: string, currentProf: ProfileData, mergeProf: ProfileData) => {
    const newValidation: MergeValidation = {
      currentAccountActive: null,
      mergeAccountActive: null,
      currentNotEmployee: null,
      mergeNotEmployee: null,
      currentEmployeeDetail: '',
      mergeEmployeeDetail: '',
      canMerge: false
    }

    // Check if accounts are active (not already merged)
    newValidation.currentAccountActive = !isAccountMerged(currentProf)
    newValidation.mergeAccountActive = !isAccountMerged(mergeProf)

    // Check employee status for both accounts in parallel
    const [currentEmp, mergeEmp] = await Promise.all([
      checkEmployeeStatus(currentMtId),
      checkEmployeeStatus(mergeMtId)
    ])

    // Unknown employee status must block the merge. Treating a failed lookup as
    // "not an employee" would allow this destructive action without validation.
    newValidation.currentNotEmployee = currentEmp.isEmployee === false
    newValidation.mergeNotEmployee = mergeEmp.isEmployee === false
    newValidation.currentEmployeeDetail = currentEmp.detail
    newValidation.mergeEmployeeDetail = mergeEmp.detail

    // Can merge only if all checks pass
    newValidation.canMerge =
      newValidation.currentAccountActive === true &&
      newValidation.mergeAccountActive === true &&
      newValidation.currentNotEmployee === true &&
      newValidation.mergeNotEmployee === true

    setValidation(newValidation)
    return newValidation
  }

  const handleLookup = async () => {
    if (!mergeEmail.trim()) {
      setError('Please enter an email to look up')
      return
    }

    setIsLoading(true)
    setError('')
    setSuccess('')
    setMergeProfile(null)
    setValidation(prev => ({ ...prev, canMerge: false }))

    try {
      // Look up the merge profile by email
      const searchResponse = await fetch(`/api/marianatek/api/users/?email=${encodeURIComponent(mergeEmail.trim())}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!searchResponse.ok) {
        throw new Error(`User lookup failed: ${searchResponse.status}`)
      }

      const searchData = await searchResponse.json()
      const users = searchData.data || []

      if (users.length === 0) {
        throw new Error('No user found with that email in MarianaTek')
      }

      const mergeUserId = users[0].id

      // Check if they're trying to merge with themselves
      if (mergeUserId === currentMarianaTekId) {
        throw new Error('Cannot merge an account with itself')
      }

      await loadProfile(mergeUserId, 'merge')

      // Run validation checks after profile loads
      // We need to wait for the profile state to update, so we get it directly
      const mergeUserData = await fetch(`/api/marianatek/api/users/${mergeUserId}/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      const mergeUserJson = await mergeUserData.json()

      const mergeProf: ProfileData = {
        ...({} as ProfileData),
        marianaTekId: mergeUserId,
        rawData: mergeUserJson.data
      }

      const currentProf: ProfileData = {
        ...({} as ProfileData),
        marianaTekId: currentMarianaTekId,
        rawData: currentProfile?.rawData
      }

      const result = await runValidation(currentMarianaTekId, mergeUserId, currentProf, mergeProf)

      if (!result.canMerge) {
        const issues: string[] = []
        if (!result.currentAccountActive) issues.push('Current account has already been merged into another account')
        if (!result.mergeAccountActive) issues.push('Target account has already been merged into another account')
        if (!result.currentNotEmployee) issues.push(`Current account is an employee account (${result.currentEmployeeDetail})`)
        if (!result.mergeNotEmployee) issues.push(`Target account is an employee account (${result.mergeEmployeeDetail})`)
        setError(`Merge blocked: ${issues.join('. ')}`)
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to look up user')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMergeClick = () => {
    setShowConfirmModal(true)
  }

  const saveMergeOperation = (operation: PersistedMergeOperation | null) => {
    if (operation) {
      const canonical: PersistedMergeOperation = {
        schemaVersion: 1,
        operationId: operation.operationId,
        keepUserId: operation.keepUserId,
        mergeUserId: operation.mergeUserId,
        status: operation.status,
        createdAt: operation.createdAt,
        ...(operation.errorCode ? { errorCode: operation.errorCode } : {}),
      }
      setActiveMergeOperation(canonical)
      localStorage.setItem(MERGE_OPERATION_STORAGE_KEY, JSON.stringify(canonical))
    } else {
      setActiveMergeOperation(null)
      localStorage.removeItem(MERGE_OPERATION_STORAGE_KEY)
    }
  }

  const applyMergeOperationStatus = async (
    operation: PersistedMergeOperation,
    data: any,
  ) => {
    if (
      data?.operation_id !== operation.operationId
      || data?.keep_user !== operation.keepUserId
      || data?.merge_user !== operation.mergeUserId
      || !GATEWAY_MERGE_OPERATION_STATUSES.has(data?.status)
    ) {
      throw new Error('The merge operation returned an invalid status. Do not submit another merge.')
    }

    const updated: PersistedMergeOperation = {
      ...operation,
      status: data.status,
      errorCode: typeof data.error_code === 'string' ? data.error_code : undefined
    }

    if (updated.status === 'succeeded') {
      saveMergeOperation(null)
      setMergeStatus('')
      setIsMerging(false)
      setError('')
      setSuccess(`Successfully merged ${operationLabel(operation)}. The immutable backup versions are retained by policy.`)
      setMergeProfile(null)
      setMergeEmail('')
      if (currentMarianaTekId) {
        await loadProfile(currentMarianaTekId, 'current')
      }
      return
    }

    if (updated.status === 'rejected') {
      saveMergeOperation(null)
      setMergeStatus('')
      setIsMerging(false)
      setError(`The merge operation was rejected${updated.errorCode ? ` (${updated.errorCode})` : ''}. No retry was sent.`)
      return
    }

    saveMergeOperation(updated)
    if (updated.status === 'in_doubt') {
      setIsMerging(false)
      setMergeStatus('')
      setError('The merge write may have reached Mariana Tek, but its result is not confirmed. Do not retry this merge. Reconcile this operation instead.')
      return
    }

    setIsMerging(true)
    setError('')
    setMergeStatus(
      updated.status === 'applying'
        ? `Merging ${operationLabel(operation)} - waiting for Mariana Tek...`
        : `Merging ${operationLabel(operation)} - operation queued...`
    )
  }

  // The operation ID and account pair survive reloads. An unfinished or
  // in-doubt operation is never expired locally because doing so could make a
  // second destructive submission look safe.
  useEffect(() => {
    localStorage.removeItem('mtMergeStatus')
    const saved = localStorage.getItem(MERGE_OPERATION_STORAGE_KEY)
    if (!saved) return
    try {
      const operation = parseMtMergeOperationStorage(saved)
      if (!operation) throw new Error('invalid operation')
      if (operation.status === 'succeeded' || operation.status === 'rejected') {
        localStorage.removeItem(MERGE_OPERATION_STORAGE_KEY)
        return
      }
      localStorage.setItem(MERGE_OPERATION_STORAGE_KEY, JSON.stringify(operation))
      setActiveMergeOperation(operation)
      if (operation.status === 'backup_failed') {
        setError('An earlier backup job failed before any merge was attempted. Clear it to start a new operation.')
        setIsMerging(false)
      } else if (operation.status === 'backup_in_doubt') {
        setError('An earlier backup write is in doubt. No merge was attempted. Reconcile the backup before starting over.')
        setIsMerging(false)
      } else if (operation.status === 'backing_up') {
        setError('An earlier backup preparation can be resumed with the same operation ID. No merge has been submitted.')
        setIsMerging(false)
      } else if (operation.status === 'in_doubt') {
        setError('An earlier merge is still in doubt. Do not retry it. Reconcile the saved operation instead.')
        setIsMerging(false)
      } else {
        setMergeStatus(`Checking saved merge operation ${operation.operationId}...`)
        setIsMerging(operation.status !== 'acceptance_unknown')
      }
    } catch {
      localStorage.removeItem(MERGE_OPERATION_STORAGE_KEY)
    }
  }, [])

  // Polling is read-only. The browser never repeats the merge POST, including
  // when the original acceptance response was lost.
  useEffect(() => {
    const operation = activeMergeOperation
    if (
      !operation
      || operation.status === 'succeeded'
      || operation.status === 'rejected'
      || operation.status === 'in_doubt'
      || operation.status === 'backing_up'
      || operation.status === 'backup_failed'
      || operation.status === 'backup_in_doubt'
      || mergeSubmissionInFlight.current
    ) {
      return undefined
    }

    let cancelled = false
    const check = async () => {
      try {
        const response = await fetch(
          `/api/marianatek/api/merge-operations/${encodeURIComponent(operation.operationId)}`,
          { method: 'GET', headers: { 'Accept': 'application/json' } }
        )
        const body = await response.json().catch(() => ({}))
        if (cancelled) return
        if (response.status === 404 && ['submitting', 'acceptance_unknown'].includes(operation.status)) {
          const unknownOperation = { ...operation, status: 'acceptance_unknown' as const }
          saveMergeOperation(unknownOperation)
          setIsMerging(false)
          setMergeStatus('')
          setError('Merge operation acceptance is not confirmed yet. Do not submit it again. Keep this operation ID for review.')
          return
        }
        if (!response.ok) {
          setMergeStatus(`Merge operation ${operation.operationId} status is temporarily unavailable. Retrying the status check only...`)
          return
        }
        await applyMergeOperationStatus(operation, body.data)
      } catch {
        if (!cancelled) {
          setMergeStatus(`Merge operation ${operation.operationId} status is temporarily unavailable. Retrying the status check only...`)
        }
      }
    }

    void check()
    const intervalId = window.setInterval(check, 3000)
    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [activeMergeOperation?.operationId, activeMergeOperation?.status])

  const handleReconcileOperation = async () => {
    const operation = activeMergeOperation
    if (!operation || operation.status !== 'in_doubt') return
    setError('')
    setMergeStatus(`Reconciling operation ${operation.operationId} with read-only account checks...`)
    try {
      const response = await fetch(
        `/api/marianatek/api/merge-operations/${encodeURIComponent(operation.operationId)}/reconcile`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({})
        }
      )
      const body = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(body.error || 'Reconciliation status is unavailable')
      await applyMergeOperationStatus(operation, body.data)
    } catch (err) {
      setMergeStatus('')
      setError(`${err instanceof Error ? err.message : 'Reconciliation status is unavailable'}. No merge retry was sent.`)
    }
  }

  const backupProofIsValid = (
    proof: any,
    operation: PersistedMergeOperation,
    role: 'keep' | 'merge',
    subjectUserId: string,
  ): proof is MergeBackupProof => (
    proof?.operation_id === operation.operationId
    && proof?.keep_user_id === operation.keepUserId
    && proof?.merge_user_id === operation.mergeUserId
    && proof?.role === role
    && proof?.subject_user_id === subjectUserId
    && typeof proof?.version_id === 'string'
    && Boolean(proof.version_id)
    && proof.version_id !== 'null'
    && typeof proof?.etag === 'string'
    && Boolean(proof.etag)
    && typeof proof?.sha256 === 'string'
    && /^[a-f0-9]{64}$/.test(proof.sha256)
  )

  const pollForBackup = async (
    operation: PersistedMergeOperation,
    jobId: string,
    label: string,
    role: 'keep' | 'merge',
    subjectUserId: string,
  ): Promise<MergeBackupProof> => {
    const startedAt = Date.now()
    const maxWaitTime = 15 * 60 * 1000
    while (Date.now() - startedAt < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, 2000))
      const response = await fetch(`/api/mt-capture/status/${encodeURIComponent(jobId)}`)
      if (response.status === 404) {
        throw new Error(`${label} backup status was not found. No merge was attempted.`)
      }
      const data = await response.json().catch(() => ({}))
      if (!response.ok) continue
      if (data.job?.status === 'completed') {
        if (!backupProofIsValid(data.job.proof, operation, role, subjectUserId)) {
          throw new Error(`${label} backup completed without a valid immutable proof. No merge was attempted.`)
        }
        return data.job.proof
      }
      if (data.job?.status === 'in_doubt') {
        const error = new Error(`${label} backup write is in doubt. No merge was attempted.`) as Error & { code?: string }
        error.code = 'MT_CAPTURE_BACKUP_IN_DOUBT'
        throw error
      }
      if (data.job?.status === 'failed') {
        const error = new Error(`${label} backup failed. No merge was attempted.`) as Error & { code?: string }
        error.code = 'MT_CAPTURE_BACKUP_FAILED'
        throw error
      }
      const elapsed = Math.round((Date.now() - startedAt) / 1000)
      setMergeStatus(`Merging ${operationLabel(operation)} - backing up (${elapsed}s)...`)
    }
    throw new Error(`${label} backup is still running. Resume operation ${operation.operationId} to keep checking.`)
  }

  const submitBackedUpMerge = async (
    operation: PersistedMergeOperation,
    keepBackup: MergeBackupProof,
    mergeBackup: MergeBackupProof,
  ) => {
    const submitting = { ...operation, status: 'submitting' as const }
    mergeSubmissionInFlight.current = true
    saveMergeOperation(submitting)
    setMergeStatus(`Merging ${operationLabel(operation)} - submitting operation ${operation.operationId} once...`)
    try {
      const response = await fetch(`/api/marianatek/api/users/${operation.keepUserId}/merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'Accept': 'application/vnd.api+json'
        },
        body: JSON.stringify({
          operation_id: operation.operationId,
          data: {
            merge_user: operation.mergeUserId
          },
          confirmation: {
            keep_user: operation.keepUserId,
            merge_user: operation.mergeUserId
          },
          backups: {
            keep: keepBackup,
            merge: mergeBackup
          }
        })
      })

      const responseData = await response.json().catch(() => ({}))
      mergeSubmissionInFlight.current = false
      if (!response.ok) {
        if (responseData.code === 'MT_MERGE_NEW_OPERATIONS_DISABLED') {
          saveMergeOperation({ ...operation, status: 'backing_up' })
          setIsMerging(false)
          setMergeStatus('')
          setError(`New merge operations are temporarily unavailable. Operation ${operation.operationId} and its immutable backups remain saved. Resume it when the service is accepting new work.`)
          return
        }
        // These codes are produced strictly before the gateway accepts an
        // operation ("No merge was attempted"), so the operation ID can be
        // discarded and the tool must not lock itself on them.
        const provablyNotAccepted = [
          'MT_MERGE_NOT_CONFIGURED',
          'MT_MERGE_VALIDATION_UNAVAILABLE',
          'MT_MERGE_GATEWAY_AUTHORIZATION_FAILED',
        ].includes(responseData.code)
        const acceptanceUnknown = !provablyNotAccepted
          && (response.status >= 500
            || responseData.code === 'MT_MERGE_OPERATION_ACCEPTANCE_UNKNOWN')
        if (acceptanceUnknown) {
          saveMergeOperation({ ...submitting, status: 'acceptance_unknown' })
          setIsMerging(false)
          setMergeStatus('')
          setError(`Operation ${operation.operationId} may have been accepted. Do not retry it. Status checks will continue with this ID.`)
          return
        }
        saveMergeOperation(null)
        setIsMerging(false)
        setMergeStatus('')
        setError(responseData.error
          || responseData.message
          || `Merge failed: ${response.status}`)
        return
      }
      await applyMergeOperationStatus(submitting, responseData.data)
    } catch {
      mergeSubmissionInFlight.current = false
      saveMergeOperation({ ...submitting, status: 'acceptance_unknown' })
      setIsMerging(false)
      setMergeStatus('')
      setError(`Operation ${operation.operationId} may have been accepted. Do not retry it. Keep this operation ID and check its status.`)
    } finally {
      mergeSubmissionInFlight.current = false
    }
  }

  const resumeBackupOperation = async (
    operation: PersistedMergeOperation,
    clearOnInitialGateRejection = false
  ) => {
    setIsMerging(true)
    setError('')
    setSuccess('')
    saveMergeOperation({ ...operation, status: 'backing_up' })
    setMergeStatus(`Merging ${operationLabel(operation)} - starting immutable backups...`)
    try {
      const requests = [
        { role: 'keep' as const, userId: operation.keepUserId },
        { role: 'merge' as const, userId: operation.mergeUserId },
      ]
      const responses = await Promise.all(requests.map((request) => fetch('/api/mt-capture/merge-capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operationId: operation.operationId,
          keepUserId: operation.keepUserId,
          mergeUserId: operation.mergeUserId,
          userId: request.userId,
          role: request.role,
        })
      })))
      const results = await Promise.all(responses.map((response) => response.json().catch(() => ({}))))
      const claimOutcome = classifyMergeBackupClaims(results.map((result, index) => ({
        responseOk: responses[index].ok,
        responseSuccess: result.success === true,
        jobId: result.jobId,
        code: result.code,
      })))
      if (claimOutcome === 'gate_disabled') {
        const error = new Error(
          'New merge operations are temporarily unavailable. No merge was attempted.'
        ) as Error & { code?: string }
        error.code = 'MT_MERGE_NEW_OPERATIONS_DISABLED'
        throw error
      }
      if (claimOutcome !== 'accepted') {
        const error = new Error(
          `Backup preparation for operation ${operation.operationId} may have been partially accepted. Keep this operation ID for recovery. No merge has been submitted.`
        ) as Error & { code?: string }
        error.code = 'MT_CAPTURE_BACKUP_ACCEPTANCE_UNKNOWN'
        throw error
      }
      const settled = await Promise.allSettled([
        pollForBackup(operation, results[0].jobId, 'Keep profile', 'keep', operation.keepUserId),
        pollForBackup(operation, results[1].jobId, 'Merge-from profile', 'merge', operation.mergeUserId),
      ])
      const rejected = settled.filter((result): result is PromiseRejectedResult => result.status === 'rejected')
      if (rejected.length > 0) {
        const inDoubt = rejected.find((result) => result.reason?.code === 'MT_CAPTURE_BACKUP_IN_DOUBT')
        if (inDoubt) throw inDoubt.reason
        const unresolved = rejected.find((result) => result.reason?.code !== 'MT_CAPTURE_BACKUP_FAILED')
        if (unresolved) throw unresolved.reason
        throw rejected[0].reason
      }
      const [keepBackup, mergeBackup] = settled.map((result) => (
        result as PromiseFulfilledResult<MergeBackupProof>
      ).value)
      await submitBackedUpMerge(operation, keepBackup, mergeBackup)
    } catch (err) {
      const code = (err as Error & { code?: string })?.code
      if (code === 'MT_MERGE_NEW_OPERATIONS_DISABLED') {
        if (clearOnInitialGateRejection) {
          saveMergeOperation(null)
        } else {
          saveMergeOperation({ ...operation, status: 'backing_up' })
        }
        setIsMerging(false)
        setMergeStatus('')
        setError(clearOnInitialGateRejection
          ? 'New merge operations are temporarily unavailable. No merge was attempted.'
          : `New merge operations are temporarily unavailable. Operation ${operation.operationId} remains saved for recovery on the active AWS service.`)
        return
      }
      const status: MergeOperationStatus = code === 'MT_CAPTURE_BACKUP_IN_DOUBT'
        ? 'backup_in_doubt'
        : code === 'MT_CAPTURE_BACKUP_FAILED'
          ? 'backup_failed'
          : 'backing_up'
      saveMergeOperation({ ...operation, status })
      setIsMerging(false)
      setMergeStatus('')
      setError(err instanceof Error ? err.message : 'Backup preparation could not be completed. No merge was attempted.')
    }
  }

  const handleReconcileBackup = async () => {
    const operation = activeMergeOperation
    if (!operation || operation.status !== 'backup_in_doubt') return
    setIsMerging(true)
    setError('')
    setMergeStatus(`Reconciling immutable backups for operation ${operation.operationId}...`)
    try {
      const jobIds = [
        `mt-merge.${operation.operationId}.keep`,
        `mt-merge.${operation.operationId}.merge`,
      ]
      const responses = await Promise.all(jobIds.map((jobId) => fetch(
        `/api/mt-capture/status/${encodeURIComponent(jobId)}/reconcile`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      )))
      const results = await Promise.all(responses.map((response) => response.json().catch(() => ({}))))
      const failedResponse = responses.findIndex((response) => !response.ok)
      if (failedResponse !== -1) {
        throw new Error(results[failedResponse].error || 'Backup reconciliation is unavailable')
      }
      const statuses = results.map((result) => result.job?.status)
      if (statuses.includes('in_doubt')) {
        throw new Error('Backup evidence is still in doubt. No merge was attempted.')
      }
      if (statuses.includes('failed')) {
        saveMergeOperation({ ...operation, status: 'backup_failed' })
        setIsMerging(false)
        setMergeStatus('')
        setError('The missing backup was confirmed before any merge was attempted. Clear this operation to start over.')
        return
      }
      saveMergeOperation({ ...operation, status: 'backing_up' })
      await resumeBackupOperation(operation)
    } catch (err) {
      saveMergeOperation({ ...operation, status: 'backup_in_doubt' })
      setIsMerging(false)
      setMergeStatus('')
      setError(err instanceof Error ? err.message : 'Backup reconciliation is unavailable')
    }
  }

  const handleMergeConfirm = async () => {
    if (!currentProfile || !mergeProfile) return
    if (activeMergeOperation) {
      setShowConfirmModal(false)
      setError(`Operation ${activeMergeOperation.operationId} must reach a confirmed terminal state before another merge can start.`)
      return
    }
    setShowConfirmModal(false)
    setIsMerging(true)
    setError('')
    setSuccess('')
    try {
      const refreshedValidation = await runValidation(
        currentProfile.marianaTekId,
        mergeProfile.marianaTekId,
        currentProfile,
        mergeProfile
      )
      if (!refreshedValidation.canMerge) {
        throw new Error('Pre-merge validation could not be confirmed. Refresh the profiles and try again.')
      }
      const operation: PersistedMergeOperation = {
        schemaVersion: 1,
        operationId: window.crypto.randomUUID(),
        keepUserId: selectedProfile === 'current' ? currentProfile.marianaTekId : mergeProfile.marianaTekId,
        mergeUserId: selectedProfile === 'current' ? mergeProfile.marianaTekId : currentProfile.marianaTekId,
        status: 'backing_up',
        createdAt: Date.now(),
      }
      // Persist the stable operation and account-role binding before either
      // backup POST. Reloads can replay the same deterministic durable claims.
      saveMergeOperation(operation)
      await resumeBackupOperation(operation, true)
    } catch (err) {
      setIsMerging(false)
      setMergeStatus('')
      setError(err instanceof Error ? err.message : 'Failed to prepare merge')
    }
  }

  const renderCreditCards = (cards: any[]) => {
    if (!cards || cards.length === 0) {
      return <FieldValue>No credit cards on file</FieldValue>
    }

    return (
      <div>
        {cards.map((card, index) => (
          <CreditCardItem key={card.id || index}>
            {card.attributes?.brand || 'Card'} ending in {card.attributes?.last_4 || '****'}
            {card.attributes?.is_default && ' (Default)'}
          </CreditCardItem>
        ))}
      </div>
    )
  }

  const renderProfile = (profile: ProfileData, type: 'current' | 'merge') => {
    const isSelected = selectedProfile === type

    return (
      <ProfileCard $type={type} $selected={isSelected}>
        <CardHeader $type={type}>
          {type === 'current' ? 'Current Profile' : 'Profile to Merge'}
        </CardHeader>

        <RadioSection onClick={() => setSelectedProfile(type)}>
          <RadioButton
            type="radio"
            name="profileSelection"
            checked={isSelected}
            onChange={() => setSelectedProfile(type)}
          />
          <RadioLabel>Merge this data</RadioLabel>
        </RadioSection>

        <Section>
          <SectionTitle>Basic Information</SectionTitle>
          <FieldRow><FieldLabel>Name:</FieldLabel><FieldValue>{profile.name}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Email:</FieldLabel><FieldValue>{profile.email}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Account Type:</FieldLabel><FieldValue>{profile.accountType}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Phone:</FieldLabel><FieldValue>{profile.phone}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Address:</FieldLabel><FieldValue>{profile.address}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Birthday:</FieldLabel><FieldValue>{profile.birthday}</FieldValue></FieldRow>
        </Section>

        <Section>
          <SectionTitle>Credit Card(s)</SectionTitle>
          {renderCreditCards(profile.creditCards)}
        </Section>

        <Section>
          <SectionTitle>Activity</SectionTitle>
          <FieldRow><FieldLabel>Last Class Taken:</FieldLabel><FieldValue>{profile.lastClassTaken}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Account Created:</FieldLabel><FieldValue>{profile.accountCreated}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Top Studio:</FieldLabel><FieldValue>{profile.topStudio}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Home Location:</FieldLabel><FieldValue>{profile.homeLocation}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Merged Details:</FieldLabel><FieldValue>{profile.mergedDetails}</FieldValue></FieldRow>
        </Section>

        <Section>
          <SectionTitle>Tags</SectionTitle>
          {profile.tags.length > 0 ? (
            <TagList>{profile.tags.map((tag, i) => <Tag key={i}>{tag}</Tag>)}</TagList>
          ) : (
            <FieldValue>No tags</FieldValue>
          )}
        </Section>

        <Section>
          <SectionTitle>Account Summary</SectionTitle>
          <FieldRow><FieldLabel>Account Balances:</FieldLabel><FieldValue>${profile.accountBalances.toFixed(2)}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Purchases:</FieldLabel><FieldValue>{profile.purchasesCount}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Upcoming Reservations:</FieldLabel><FieldValue>{profile.upcomingReservationsCount}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Classes Taken:</FieldLabel><FieldValue>{profile.classesTakenCount}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Credits:</FieldLabel><FieldValue>{profile.creditsCount}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Active Memberships:</FieldLabel><FieldValue>{profile.activeMembershipsCount}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Note(s):</FieldLabel><FieldValue>{profile.notesCount}</FieldValue></FieldRow>
        </Section>
      </ProfileCard>
    )
  }

  const renderMergeResult = () => {
    if (!currentProfile || !mergeProfile) return null

    const selectedProfileData = selectedProfile === 'current' ? currentProfile : mergeProfile

    // Calculate totals
    const totalAccountBalances = currentProfile.accountBalances + mergeProfile.accountBalances
    const totalPurchases = currentProfile.purchasesCount + mergeProfile.purchasesCount
    const totalUpcomingReservations = currentProfile.upcomingReservationsCount + mergeProfile.upcomingReservationsCount
    const totalClassesTaken = currentProfile.classesTakenCount + mergeProfile.classesTakenCount
    const totalCredits = currentProfile.creditsCount + mergeProfile.creditsCount
    const totalActiveMemberships = currentProfile.activeMembershipsCount + mergeProfile.activeMembershipsCount
    const totalNotes = currentProfile.notesCount + mergeProfile.notesCount

    return (
      <ProfileCard $type="result">
        <CardHeader $type="result">
          Merge Result Preview
        </CardHeader>
        <RadioSpacer />

        <Section>
          <SectionTitle>Selected Profile Data</SectionTitle>
          <FieldRow><FieldLabel>Name:</FieldLabel><FieldValue>{selectedProfileData.name}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Email:</FieldLabel><FieldValue>{selectedProfileData.email}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Account Type:</FieldLabel><FieldValue>{selectedProfileData.accountType}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Phone:</FieldLabel><FieldValue>{selectedProfileData.phone}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Address:</FieldLabel><FieldValue>{selectedProfileData.address}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Birthday:</FieldLabel><FieldValue>{selectedProfileData.birthday}</FieldValue></FieldRow>
        </Section>

        <Section>
          <SectionTitle>Credit Card(s)</SectionTitle>
          {renderCreditCards(selectedProfileData.creditCards)}
        </Section>

        <Section>
          <SectionTitle>Activity</SectionTitle>
          <FieldRow><FieldLabel>Last Class Taken:</FieldLabel><FieldValue>{selectedProfileData.lastClassTaken}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Account Created:</FieldLabel><FieldValue>{selectedProfileData.accountCreated}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Top Studio:</FieldLabel><FieldValue>{selectedProfileData.topStudio}</FieldValue></FieldRow>
          <FieldRow><FieldLabel>Home Location:</FieldLabel><FieldValue>{selectedProfileData.homeLocation}</FieldValue></FieldRow>
        </Section>

        <Section>
          <SectionTitle>Tags</SectionTitle>
          {selectedProfileData.tags.length > 0 ? (
            <TagList>{selectedProfileData.tags.map((tag, i) => <Tag key={i}>{tag}</Tag>)}</TagList>
          ) : (
            <FieldValue>No tags</FieldValue>
          )}
        </Section>

        <Section>
          <SectionTitle>Combined Totals</SectionTitle>
          <TotalRow>
            <FieldLabel>Account Balances:</FieldLabel>
            <TotalValue>${totalAccountBalances.toFixed(2)}</TotalValue>
          </TotalRow>
          <TotalRow>
            <FieldLabel>Purchases:</FieldLabel>
            <TotalValue>{totalPurchases}</TotalValue>
          </TotalRow>
          <TotalRow>
            <FieldLabel>Upcoming Reservations:</FieldLabel>
            <TotalValue>{totalUpcomingReservations}</TotalValue>
          </TotalRow>
          <TotalRow>
            <FieldLabel>Classes Taken:</FieldLabel>
            <TotalValue>{totalClassesTaken}</TotalValue>
          </TotalRow>
          <TotalRow>
            <FieldLabel>Credits:</FieldLabel>
            <TotalValue>{totalCredits}</TotalValue>
          </TotalRow>
          <TotalRow>
            <FieldLabel>Active Memberships:</FieldLabel>
            <TotalValue>{totalActiveMemberships}</TotalValue>
          </TotalRow>
          <TotalRow>
            <FieldLabel>Note(s):</FieldLabel>
            <TotalValue>{totalNotes}</TotalValue>
          </TotalRow>
        </Section>
      </ProfileCard>
    )
  }

  return (
    <MergeContainer>
      <MergeTitle>MT Merge Tool</MergeTitle>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      {activeMergeOperation && [
        'backing_up',
        'backup_failed',
        'backup_in_doubt',
        'acceptance_unknown',
        'in_doubt',
      ].includes(activeMergeOperation.status) && (
        <WarningBanner>
          <div>
            <div>Operation {activeMergeOperation.operationId}</div>
            <div>
              {activeMergeOperation.status === 'backing_up'
                ? 'Backup preparation can resume with this same operation ID. No merge has been submitted.'
                : activeMergeOperation.status === 'backup_failed'
                  ? 'A backup job failed before any merge was attempted. Clear it before starting a new operation.'
                : activeMergeOperation.status === 'backup_in_doubt'
                  ? 'A backup write is ambiguous. No merge was attempted. Do not start another operation.'
                  : activeMergeOperation.status === 'in_doubt'
                    ? 'The merge write outcome is ambiguous. Do not retry this merge.'
                    : 'Acceptance is not confirmed. Only status checks are being retried.'}
            </div>
          </div>
          {activeMergeOperation.status === 'backing_up' && (
            <Button onClick={() => resumeBackupOperation(activeMergeOperation)} disabled={isMerging}>
              {isMerging ? 'Checking backups...' : 'Resume backups'}
            </Button>
          )}
          {activeMergeOperation.status === 'backup_failed' && (
            <Button onClick={() => { saveMergeOperation(null); setError('') }} disabled={isMerging}>
              Clear failed backup
            </Button>
          )}
          {activeMergeOperation.status === 'backup_in_doubt' && (
            <Button onClick={handleReconcileBackup} disabled={isMerging}>
              {isMerging ? 'Reconciling...' : 'Reconcile backups'}
            </Button>
          )}
          {activeMergeOperation.status === 'in_doubt' && (
            <Button onClick={handleReconcileOperation} disabled={Boolean(mergeStatus)}>
              {mergeStatus ? 'Reconciling...' : 'Reconcile status'}
            </Button>
          )}
        </WarningBanner>
      )}

      <InputSection>
        <FormGroup>
          <Label htmlFor="mergeEmail">Profile to Merge (Email):</Label>
          <Input
            id="mergeEmail"
            type="email"
            value={mergeEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMergeEmail(e.target.value)}
            placeholder="Enter email of profile to merge"
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleLookup()}
          />
        </FormGroup>
        <ButtonGroup>
          <Button onClick={handleLookup} primary disabled={isLoading || !mergeEmail.trim() || Boolean(activeMergeOperation)}>
            {isLoading ? 'Looking up...' : 'Lookup'}
          </Button>
          {mergeProfile && validation.canMerge && (
            <MergeButton onClick={handleMergeClick} primary disabled={isMerging || Boolean(activeMergeOperation)}>
              {isMerging ? 'Merging...' : 'Merge'}
            </MergeButton>
          )}
        </ButtonGroup>
      </InputSection>

      {mergeProfile && !validation.canMerge && !isLoading && (
        <BlockBanner>
          ⛔ This merge is blocked. Employee accounts (active or inactive) cannot be merged. Please review the validation results below.
        </BlockBanner>
      )}

      {mergeProfile && !isLoading && (
        <ValidationSection>
          <SectionTitle>Pre-Merge Validation</SectionTitle>
          <ValidationRow $status={validation.currentAccountActive === true ? 'pass' : validation.currentAccountActive === false ? 'fail' : 'loading'}>
            <ValidationIcon $status={validation.currentAccountActive === true ? 'pass' : validation.currentAccountActive === false ? 'fail' : 'loading'}>
              {validation.currentAccountActive === true ? '✅' : validation.currentAccountActive === false ? '❌' : '⏳'}
            </ValidationIcon>
            <ValidationLabel>Current account is active (not merged)</ValidationLabel>
            {validation.currentAccountActive === false && <ValidationDetail>Account has been merged into another account</ValidationDetail>}
          </ValidationRow>
          <ValidationRow $status={validation.mergeAccountActive === true ? 'pass' : validation.mergeAccountActive === false ? 'fail' : 'loading'}>
            <ValidationIcon $status={validation.mergeAccountActive === true ? 'pass' : validation.mergeAccountActive === false ? 'fail' : 'loading'}>
              {validation.mergeAccountActive === true ? '✅' : validation.mergeAccountActive === false ? '❌' : '⏳'}
            </ValidationIcon>
            <ValidationLabel>Target account is active (not merged)</ValidationLabel>
            {validation.mergeAccountActive === false && <ValidationDetail>Account has been merged into another account</ValidationDetail>}
          </ValidationRow>
          <ValidationRow $status={validation.currentNotEmployee === true ? 'pass' : validation.currentNotEmployee === false ? 'fail' : 'loading'}>
            <ValidationIcon $status={validation.currentNotEmployee === true ? 'pass' : validation.currentNotEmployee === false ? 'fail' : 'loading'}>
              {validation.currentNotEmployee === true ? '✅' : validation.currentNotEmployee === false ? '❌' : '⏳'}
            </ValidationIcon>
            <ValidationLabel>Current account is not an employee</ValidationLabel>
            {validation.currentNotEmployee === false && <ValidationDetail>{validation.currentEmployeeDetail}</ValidationDetail>}
          </ValidationRow>
          <ValidationRow $status={validation.mergeNotEmployee === true ? 'pass' : validation.mergeNotEmployee === false ? 'fail' : 'loading'}>
            <ValidationIcon $status={validation.mergeNotEmployee === true ? 'pass' : validation.mergeNotEmployee === false ? 'fail' : 'loading'}>
              {validation.mergeNotEmployee === true ? '✅' : validation.mergeNotEmployee === false ? '❌' : '⏳'}
            </ValidationIcon>
            <ValidationLabel>Target account is not an employee</ValidationLabel>
            {validation.mergeNotEmployee === false && <ValidationDetail>{validation.mergeEmployeeDetail}</ValidationDetail>}
          </ValidationRow>
        </ValidationSection>
      )}

      {(isLoading || isMerging) && (
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingText>{isMerging ? (mergeStatus || 'Merging profiles...') : 'Loading profile data...'}</LoadingText>
        </LoadingOverlay>
      )}

      {!isLoading && !isMerging && currentProfile && mergeProfile && (
        <ProfilesContainer>
          {renderProfile(currentProfile, 'current')}
          {renderProfile(mergeProfile, 'merge')}
          {renderMergeResult()}
        </ProfilesContainer>
      )}

      {!isLoading && !isMerging && currentProfile && !mergeProfile && (
        <ProfilesContainer style={{ gridTemplateColumns: '1fr' }}>
          <ProfileCard $type="current">
            <CardHeader $type="current">Current Profile</CardHeader>
            <Section>
              <SectionTitle>Basic Information</SectionTitle>
              <FieldRow><FieldLabel>Name:</FieldLabel><FieldValue>{currentProfile.name}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Email:</FieldLabel><FieldValue>{currentProfile.email}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Account Type:</FieldLabel><FieldValue>{currentProfile.accountType}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Phone:</FieldLabel><FieldValue>{currentProfile.phone}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Address:</FieldLabel><FieldValue>{currentProfile.address}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Birthday:</FieldLabel><FieldValue>{currentProfile.birthday}</FieldValue></FieldRow>
            </Section>
            <Section>
              <SectionTitle>Credit Card(s)</SectionTitle>
              {renderCreditCards(currentProfile.creditCards)}
            </Section>
            <Section>
              <SectionTitle>Activity</SectionTitle>
              <FieldRow><FieldLabel>Last Class Taken:</FieldLabel><FieldValue>{currentProfile.lastClassTaken}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Account Created:</FieldLabel><FieldValue>{currentProfile.accountCreated}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Top Studio:</FieldLabel><FieldValue>{currentProfile.topStudio}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Home Location:</FieldLabel><FieldValue>{currentProfile.homeLocation}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Merged Details:</FieldLabel><FieldValue>{currentProfile.mergedDetails}</FieldValue></FieldRow>
            </Section>
            <Section>
              <SectionTitle>Tags</SectionTitle>
              {currentProfile.tags.length > 0 ? (
                <TagList>{currentProfile.tags.map((tag, i) => <Tag key={i}>{tag}</Tag>)}</TagList>
              ) : (
                <FieldValue>No tags</FieldValue>
              )}
            </Section>
            <Section>
              <SectionTitle>Account Summary</SectionTitle>
              <FieldRow><FieldLabel>Account Balances:</FieldLabel><FieldValue>${currentProfile.accountBalances.toFixed(2)}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Purchases:</FieldLabel><FieldValue>{currentProfile.purchasesCount}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Upcoming Reservations:</FieldLabel><FieldValue>{currentProfile.upcomingReservationsCount}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Classes Taken:</FieldLabel><FieldValue>{currentProfile.classesTakenCount}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Credits:</FieldLabel><FieldValue>{currentProfile.creditsCount}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Active Memberships:</FieldLabel><FieldValue>{currentProfile.activeMembershipsCount}</FieldValue></FieldRow>
              <FieldRow><FieldLabel>Note(s):</FieldLabel><FieldValue>{currentProfile.notesCount}</FieldValue></FieldRow>
            </Section>
          </ProfileCard>
        </ProfilesContainer>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ModalOverlay onClick={() => setShowConfirmModal(false)}>
          <ModalContent onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <ModalTitle>Confirm Merge</ModalTitle>
            <ModalText>
              This process is not reversible. Please ensure you have the correct profiles selected.
              <br /><br />
              The data from the <strong>{selectedProfile === 'current' ? 'merge' : 'current'}</strong> profile will be merged into the <strong>{selectedProfile}</strong> profile.
              <br /><br />
              The merge will be queued only after both accounts have complete, immutable, version-bound backups.
            </ModalText>
            <ModalButtonGroup>
              <Button onClick={() => setShowConfirmModal(false)}>
                Go Back
              </Button>
              <MergeButton onClick={handleMergeConfirm} primary>
                Proceed
              </MergeButton>
            </ModalButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </MergeContainer>
  )
}

export default MTMerge
